import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { peoplePulseDataService } from '../utils/peoplePulseDataService';
import '../styles/GoalsOKR.css';

function GoalsOKRs() {
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [filteredGoals, setFilteredGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [goalFormData, setGoalFormData] = useState({
    title: '',
    description: '',
    type: 'individual',
    category: 'revenue',
    dueDate: '',
    assignedTo: '',
    parentGoal: '',
    keyResults: [{ description: '', targetValue: '', currentValue: 0, unit: '%' }]
  });

  const [filters, setFilters] = useState({
    search: '',
    type: '',
    category: '',
    status: ''
  });

  useEffect(() => {
    loadGoals();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goals, filters]);

  const loadGoals = async () => {
    try {
      setLoading(true);
      const data = await peoplePulseDataService.getGoals();
      setGoals(data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load goals:', err);
      setError('Failed to load goals: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...goals];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(goal =>
        goal.title?.toLowerCase().includes(searchLower) ||
        goal.description?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.type) {
      filtered = filtered.filter(goal => goal.type === filters.type);
    }

    if (filters.category) {
      filtered = filtered.filter(goal => goal.category === filters.category);
    }

    if (filters.status) {
      filtered = filtered.filter(goal => goal.status === filters.status);
    }

    setFilteredGoals(filtered);
  };

  const handleCreateGoal = async (e) => {
    e.preventDefault();
    try {
      const newGoal = await peoplePulseDataService.createGoal(goalFormData);
      setGoals([newGoal, ...goals]);
      setShowCreateModal(false);
      resetForm();
      setSuccessMessage('Goal created successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to create goal: ' + err.message);
    }
  };

  const handleUpdateProgress = async (goalId, progress) => {
    try {
      await peoplePulseDataService.updateGoalProgress(goalId, progress);
      setGoals(goals.map(g => g.id === goalId ? { ...g, progress } : g));
      setSuccessMessage('Progress updated!');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      setError('Failed to update progress: ' + err.message);
    }
  };

  const addKeyResult = () => {
    setGoalFormData({
      ...goalFormData,
      keyResults: [...goalFormData.keyResults, { description: '', targetValue: '', currentValue: 0, unit: '%' }]
    });
  };

  const removeKeyResult = (index) => {
    const newKeyResults = goalFormData.keyResults.filter((_, i) => i !== index);
    setGoalFormData({ ...goalFormData, keyResults: newKeyResults });
  };

  const resetForm = () => {
    setGoalFormData({
      title: '',
      description: '',
      type: 'individual',
      category: 'revenue',
      dueDate: '',
      assignedTo: '',
      parentGoal: '',
      keyResults: [{ description: '', targetValue: '', currentValue: 0, unit: '%' }]
    });
    setEditingGoal(null);
  };

  const getProgressColor = (progress) => {
    if (progress >= 90) return '#10B981';
    if (progress >= 70) return '#3B82F6';
    if (progress >= 50) return '#F59E0B';
    return '#EF4444';
  };

  if (loading) {
    return (
      <div className="hr-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading goals...</p>
      </div>
    );
  }

  return (
    <div className="hr-dashboard-container peoplepulse-page">
      <div className="hr-dashboard-header">
        <div>
          <h1>Goals & OKRs</h1>
          <p className="subtitle">{filteredGoals.length} goal(s) tracked</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
            + Create Goal
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <span>{successMessage}</span>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search goals..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="search-input"
        />

        <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
          <option value="">All Types</option>
          <option value="individual">Individual</option>
          <option value="team">Team</option>
          <option value="company">Company</option>
        </select>

        <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
          <option value="">All Categories</option>
          <option value="revenue">Revenue</option>
          <option value="customer">Customer Success</option>
          <option value="product">Product</option>
          <option value="operations">Operations</option>
          <option value="development">Team Development</option>
        </select>

        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="at-risk">At Risk</option>
          <option value="overdue">Overdue</option>
        </select>

        <button className="btn-secondary" onClick={() => setFilters({ search: '', type: '', category: '', status: '' })}>
          Clear Filters
        </button>
      </div>

      {/* Goals Grid */}
      <div className="goals-grid">
        {filteredGoals.length === 0 ? (
          <div className="empty-state">
            <p>No goals found</p>
            <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
              Create Your First Goal
            </button>
          </div>
        ) : (
          filteredGoals.map((goal) => (
            <div key={goal.id} className="goal-card">
              <div className="goal-header">
                <div>
                  <h3>{goal.title}</h3>
                  <span className={`badge goal-type-${goal.type}`}>{goal.type}</span>
                  <span className={`badge goal-category-${goal.category}`}>{goal.category}</span>
                </div>
                <div className="goal-actions">
                  <button className="btn-link" onClick={() => navigate(`/teamflect/goals/${goal.id}`)}>
                    View Details
                  </button>
                </div>
              </div>

              <p className="goal-description">{goal.description}</p>

              <div className="goal-progress-section">
                <div className="progress-header">
                  <span>Progress</span>
                  <span className="progress-value" style={{ color: getProgressColor(goal.progress || 0) }}>
                    {goal.progress || 0}%
                  </span>
                </div>
                <div className="progress-bar-container">
                  <div
                    className="progress-bar-fill"
                    style={{
                      width: `${goal.progress || 0}%`,
                      backgroundColor: getProgressColor(goal.progress || 0)
                    }}
                  ></div>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={goal.progress || 0}
                  onChange={(e) => handleUpdateProgress(goal.id, parseInt(e.target.value))}
                  className="progress-slider"
                />
              </div>

              {goal.keyResults && goal.keyResults.length > 0 && (
                <div className="key-results">
                  <h4>Key Results</h4>
                  {goal.keyResults.map((kr, index) => (
                    <div key={index} className="key-result-item">
                      <span>{kr.description}</span>
                      <span className="kr-progress">{kr.currentValue || 0}/{kr.targetValue} {kr.unit}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="goal-footer">
                <span>Due: {new Date(goal.dueDate).toLocaleDateString()}</span>
                <span>Owner: {goal.assignedTo || 'Unassigned'}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Goal Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingGoal ? 'Edit Goal' : 'Create New Goal'}</h2>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }}>×</button>
            </div>

            <form onSubmit={handleCreateGoal}>
              <div className="form-group">
                <label>Goal Title *</label>
                <input
                  type="text"
                  value={goalFormData.title}
                  onChange={(e) => setGoalFormData({ ...goalFormData, title: e.target.value })}
                  required
                  placeholder="e.g., Increase monthly revenue by 25%"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={goalFormData.description}
                  onChange={(e) => setGoalFormData({ ...goalFormData, description: e.target.value })}
                  rows="3"
                  placeholder="Describe the goal and its importance..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Type *</label>
                  <select
                    value={goalFormData.type}
                    onChange={(e) => setGoalFormData({ ...goalFormData, type: e.target.value })}
                    required
                  >
                    <option value="individual">Individual Goal</option>
                    <option value="team">Team Goal</option>
                    <option value="company">Company Goal</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={goalFormData.category}
                    onChange={(e) => setGoalFormData({ ...goalFormData, category: e.target.value })}
                    required
                  >
                    <option value="revenue">Revenue</option>
                    <option value="customer">Customer Success</option>
                    <option value="product">Product</option>
                    <option value="operations">Operations</option>
                    <option value="development">Team Development</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Due Date *</label>
                  <input
                    type="date"
                    value={goalFormData.dueDate}
                    onChange={(e) => setGoalFormData({ ...goalFormData, dueDate: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Assigned To</label>
                  <input
                    type="text"
                    value={goalFormData.assignedTo}
                    onChange={(e) => setGoalFormData({ ...goalFormData, assignedTo: e.target.value })}
                    placeholder="Enter name or email"
                  />
                </div>
              </div>

              <div className="key-results-section">
                <div className="section-header">
                  <h3>Key Results (OKRs)</h3>
                  <button type="button" className="btn-secondary small" onClick={addKeyResult}>
                    + Add Key Result
                  </button>
                </div>

                {goalFormData.keyResults.map((kr, index) => (
                  <div key={index} className="key-result-form">
                    <div className="form-group">
                      <label>Description *</label>
                      <input
                        type="text"
                        value={kr.description}
                        onChange={(e) => {
                          const newKRs = [...goalFormData.keyResults];
                          newKRs[index].description = e.target.value;
                          setGoalFormData({ ...goalFormData, keyResults: newKRs });
                        }}
                        required
                        placeholder="e.g., Achieve $500K in new sales"
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Target Value *</label>
                        <input
                          type="number"
                          value={kr.targetValue}
                          onChange={(e) => {
                            const newKRs = [...goalFormData.keyResults];
                            newKRs[index].targetValue = e.target.value;
                            setGoalFormData({ ...goalFormData, keyResults: newKRs });
                          }}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Unit</label>
                        <select
                          value={kr.unit}
                          onChange={(e) => {
                            const newKRs = [...goalFormData.keyResults];
                            newKRs[index].unit = e.target.value;
                            setGoalFormData({ ...goalFormData, keyResults: newKRs });
                          }}
                        >
                          <option value="%">%</option>
                          <option value="$">$</option>
                          <option value="#">#</option>
                          <option value="points">Points</option>
                        </select>
                      </div>

                      <button
                        type="button"
                        className="btn-danger small"
                        onClick={() => removeKeyResult(index)}
                        disabled={goalFormData.keyResults.length === 1}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingGoal ? 'Update Goal' : 'Create Goal'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default GoalsOKRs;

